/**
 * Утиліта для роботи з мовою, яку користувач вручну обрав.
 * 
 * Ця мова відрізняється від мови інтерфейсу Telegram і зберігається
 * в localStorage після того, як користувач вибрав її через LanguageSwitcher.
 * 
 * Важливо: i18next автоматично зберігає мову в localStorage навіть якщо
 * вона була автоматично визначена. Тому ми використовуємо окремий прапорець
 * для відстеження, чи користувач вручну вибрав мову.
 */

import {
  USER_LANGUAGE_STORAGE_KEY,
  USER_MANUALLY_SELECTED_FLAG_KEY,
} from "./i18nStorageKeys";
import { patchOnboardingStateOnServer } from "./net/onboardingApi";

/** Реекспорт для i18n та інших модулів. */
export { USER_LANGUAGE_STORAGE_KEY } from "./i18nStorageKeys";

/**
 * Отримує мову, яку користувач вручну обрав.
 * 
 * Повертає мову тільки якщо користувач явно вибрав її через LanguageSwitcher.
 * Якщо мова була автоматично встановлена (з Telegram або браузера), повертає undefined.
 * 
 * @returns Код мови (наприклад, "uk", "en", "de") або undefined, якщо мова не вибрана вручну
 */
export const getUserSelectedLanguage = (): string | undefined => {
  try {
    // Перевіряємо, чи є прапорець, що мова була вибрана вручну
    const isManuallySelected = localStorage.getItem(USER_MANUALLY_SELECTED_FLAG_KEY) === "true";
    if (!isManuallySelected) {
      return undefined;
    }
    
    // Якщо прапорець встановлений, повертаємо мову
    return localStorage.getItem(USER_LANGUAGE_STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
};

/**
 * Лише localStorage + прапорець ручного вибору. Без PATCH на бекенд.
 * Потрібен для LanguageSelectionScreen, який одним PATCH пише ще й language_selection_shown
 * (інакше два паралельні PATCH після скидання онбордингу дають гонку на INSERT рядка).
 */
export const persistUserSelectedLanguageLocally = (languageCode: string): void => {
  try {
    localStorage.setItem(USER_LANGUAGE_STORAGE_KEY, languageCode);
    localStorage.setItem(USER_MANUALLY_SELECTED_FLAG_KEY, "true");
  } catch {
    // Ігноруємо помилки запису в localStorage
  }
};

/**
 * Встановлює мову, яку користувач вручну обрав.
 *
 * Встановлює і мову, і прапорець, що мова була вибрана вручну, і синхронізує з бекендом.
 *
 * @param languageCode - Код мови (наприклад, "uk", "en", "de")
 */
export const setUserSelectedLanguage = (languageCode: string): void => {
  persistUserSelectedLanguageLocally(languageCode);
  patchOnboardingStateOnServer({
    i18next_lng: languageCode,
    user_manually_selected_language: true,
  }).catch(() => {});
};

/**
 * Перевіряє, чи користувач вручну вибрав мову.
 * 
 * @returns true, якщо мова вибрана вручну, інакше false
 */
export const hasUserSelectedLanguage = (): boolean => {
  try {
    return localStorage.getItem(USER_MANUALLY_SELECTED_FLAG_KEY) === "true";
  } catch {
    return false;
  }
};
